import { Link } from 'react-router-dom';
import { Unlink } from 'lucide-react';
import type { DashboardDetachedState } from '@/api/types';
import { Pill } from '@/components/widgets/Pill';
import { fmtDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDetachState } from './detach-state';

/** 徽标 title(悬停)文案:把"真实口径归零 ≠ 风险归零"这句话带在手边。 */
function detachTitle(state: DashboardDetachedState): string {
  const since = state.since ? fmtDateTime(state.since) : '——';
  return [
    `虚实已脱钩(自 ${since})`,
    `仍在持的脱钩持仓 ${state.legs} 条:不再对冲、平仓不发真实单、按中价结算,平台承担其全部盈亏。`,
    '真实账户口径的保证金占用 / 净敞口 / 基差都不覆盖这部分,归零不等于风险归零。',
    '脱钩态在当晚兜底结算后自然归零。',
  ].join('\n');
}

/**
 * 脱钩徽标(展示型)。未脱钩返回 null —— 稳态下完全不占位。
 * 数据由调用方传入(页面通常已有 dashboard 数据,不必再拉一份)。
 */
export function DetachedBadge({
  state,
  className,
}: {
  state: DashboardDetachedState;
  className?: string;
}) {
  if (!state.active) return null;
  return (
    <Pill tone="down" className={cn('whitespace-nowrap', className)} title={detachTitle(state)}>
      <Unlink className="h-3 w-3" />
      虚实已脱钩
      <span className="num">· {state.legs} 条</span>
    </Pill>
  );
}

/**
 * 常驻全局徽标(TopBar 用):自带取数,点击跳资金账户大屏看裸敞口明细。
 * 不常驻的话,"系统仍在跑但口径已断"这个状态一切页就完全不可见,管理员会忘记自己处在脱钩态。
 *
 * 三态渲染:确认脱钩 → 红标;**状态未知**(后端聚合失败返回 null)→ warn 标,不给"安全"暗示;
 * 确认未脱钩 → 不占位。把"读不到"渲染成"没脱钩"正是最需要徽标时它却消失的成因。
 */
export function GlobalDetachedBadge() {
  const state = useDetachState();
  if (state.unknown) {
    return (
      <Link to="/live" title={'脱钩态未知:后端脱钩敞口聚合本轮读取失败。\n这不等于未脱钩 —— 请到资金账户大屏核实真实持仓与未对冲敞口。'}>
        <Pill tone="warn" className="whitespace-nowrap hover:bg-warn/25">
          <Unlink className="h-3 w-3" />
          脱钩态未知
        </Pill>
      </Link>
    );
  }
  if (!state.active) return null;
  return (
    <Link to="/live" title="查看真实账户持仓与未对冲敞口明细">
      <DetachedBadge state={state} className="hover:bg-down/25" />
    </Link>
  );
}
